import { useState } from 'react';
import { useTenants, useDeleteTenant } from '../hooks/useTenants';
import TenantForm from './TenantForm';
import TierBadge from './TierBadge';
import ProvisionBillingAction from './ProvisionBillingAction';
import type { Tenant } from '@policy-tracker/types';

interface TenantListProps {
    onEdit?: (tenant: Tenant) => void;
}

export default function TenantList({ onEdit }: TenantListProps) {
    const [showCreateForm, setShowCreateForm] = useState(false);
    const { data: tenantsData, isLoading, error } = useTenants();
    const deleteTenant = useDeleteTenant();

    const handleDelete = async (tenant: Tenant) => {
        if (window.confirm(`Are you sure you want to delete tenant "${tenant.name}"?`)) {
            try {
                await deleteTenant.mutateAsync(tenant.tenantId);
            } catch (error) {
                alert('Failed to delete tenant. Please try again.');
            }
        }
    };

    const handleCreateSuccess = () => {
        setShowCreateForm(false);
    };

    if (isLoading) {
        return <div className="loading">Loading tenants...</div>;
    }

    if (error) {
        return (
            <div className="card">
                <h2>Error Loading Tenants</h2>
                <p>Unable to load tenants. Please try refreshing the page.</p>
            </div>
        );
    }

    const tenants = tenantsData?.items || [];

    return (
        <div>
            <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h2>Tenants</h2>
                    <button className="btn" onClick={() => setShowCreateForm(!showCreateForm)}>
                        {showCreateForm ? 'Cancel' : 'Add Tenant'}
                    </button>
                </div>

                {showCreateForm && (
                    <div style={{ marginBottom: '2rem' }}>
                        <h3>Create New Tenant</h3>
                        <TenantForm onSuccess={handleCreateSuccess} />
                    </div>
                )}

                {tenants.length === 0 ? (
                    <p>No tenants found. Create your first tenant to get started.</p>
                ) : (
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Sub Domain</th>
                                <th>AWS Account</th>
                                <th>Region</th>
                                <th>Tier</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tenants.map((tenant: Tenant) => (
                                <tr key={tenant.tenantId}>
                                    <td>{tenant.name}</td>
                                    <td>{tenant.subDomain}</td>
                                    <td>{tenant.awsAccount}</td>
                                    <td>{tenant.region}</td>
                                    <td>
                                        <TierBadge tenantId={tenant.tenantId} />
                                    </td>
                                    <td>{new Date(tenant.createdAt).toLocaleDateString()}</td>
                                    <td>
                                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                            {onEdit && (
                                                <button
                                                    className="btn btn-outline"
                                                    onClick={() => onEdit(tenant)}
                                                    style={{ fontSize: '12px', padding: '0.25rem 0.5rem' }}
                                                >
                                                    Edit
                                                </button>
                                            )}
                                            <ProvisionBillingAction tenant={tenant} />
                                            <button
                                                className="btn btn-danger"
                                                onClick={() => handleDelete(tenant)}
                                                disabled={deleteTenant.isPending}
                                                style={{ fontSize: '12px', padding: '0.25rem 0.5rem' }}
                                            >
                                                {deleteTenant.isPending ? 'Deleting...' : 'Delete'}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
